import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCreateTask, useItems, useAllUsers } from '../hooks/useQueries';
import Modal from './Popup/Modal';
import Select from 'react-select';
import { formatItemRef } from '../utils/itemIdFormat';
import { datePickerTriggers, isValidDateInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateUtils';

const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return value;
};

const parseChildOf = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  try {
    if (trimmed.startsWith('[')) {
      return JSON.parse(trimmed);
    }
  } catch (error) {
    console.warn('Failed to parse child_of JSON string:', value, error);
  }

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    const content = trimmed.slice(1, -1);
    if (!content) {
      return [];
    }
    return content.split(',').map(entry => {
      const normalized = normalizeId(entry.trim());
      return normalized === null ? entry.trim() : normalized;
    });
  }

  const normalized = normalizeId(trimmed);
  return normalized === null ? [] : [normalized];
};

const createParentOption = (item) => {
  if (!item) {
    return null;
  }

  const normalizedId = normalizeId(item.id);
  if (normalizedId === null) {
    return null;
  }

  const childOfValues = parseChildOf(item.child_of);
  const isChild = childOfValues.length > 0;
  const titleRaw = item.title;
  const ownerRaw = item.owner;
  const descriptionRaw = item.description;
  const itemTitle = titleRaw === null || titleRaw === undefined ? 'Untitled Item' : titleRaw.toString().trim() || 'Untitled Item';
  const ownerString = ownerRaw === null || ownerRaw === undefined ? '' : ownerRaw.toString().trim();
  const ownerName = ownerString || 'Unassigned';
  const description = descriptionRaw === null || descriptionRaw === undefined ? '' : descriptionRaw.toString().trim();

  return {
    value: normalizedId,
    label: `${formatItemRef(normalizedId)} - ${itemTitle}`,
    itemTitle,
    owner: ownerName,
    description,
    isChild
  };
};

const CreateTaskModal = ({ 
  show, 
  onClose,
  onHide,
  preSelectedParentId = '', 
  preSelectedParentTitle = '',
  onTaskCreated,
  taskDetailUrlBuilder
}) => {
  const navigate = useNavigate();
  const [selectedParentId, setSelectedParentId] = useState(() => normalizeId(preSelectedParentId));
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState(null); // null for Select component
  const [isLockedToParent, setIsLockedToParent] = useState(false);
  const [lockedParentContext, setLockedParentContext] = useState(null);
  const normalizedSelectedParentId = normalizeId(selectedParentId);

  // Determine which close handler to use (support both onClose and onHide)
  const closeModal = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    } else if (typeof onHide === 'function') {
      onHide();
    }
  }, [onClose, onHide]);

  // React Query hooks
  const { data: items = [] } = useItems();
  const { data: users = [], isLoading: usersLoading, error: usersError } = useAllUsers();
  const createTaskMutation = useCreateTask();

  // Create options for parent item dropdown
  const parentItemOptions = useMemo(() => {
    return items
      .map(createParentOption)
      .filter(Boolean);
  }, [items]);

  // Create options for assigned to dropdown
  const userOptions = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return [];
    }
    
    return users
      .filter(user => {
        // More flexible filtering - accept users with any role or no roles defined
        return user && (user.name || user.email) && 
               (!user.roles || user.roles.length === 0 || 
                user.roles.some(role => ['user', 'endorser', 'admin'].includes(role.name)));
      })
      .map(user => ({
        value: user.name || user.email,
        label: `${user.name || user.email} ${user.email && user.name ? `(${user.email})` : ''}`
      }));
  }, [users]);

  const selectedParentOption = useMemo(() => {
    if (normalizedSelectedParentId === null) {
      return null;
    }

    const option = parentItemOptions.find(opt => opt.value === normalizedSelectedParentId);
    if (option) {
      return option;
    }

    if (lockedParentContext && lockedParentContext.parentId === normalizedSelectedParentId) {
      const parentItem = items.find(item => normalizeId(item.id) === normalizedSelectedParentId);
      const fallbackOption = createParentOption(parentItem);

      if (fallbackOption) {
        return fallbackOption;
      }

      return {
        value: normalizedSelectedParentId,
        label: `${formatItemRef(normalizedSelectedParentId)} - ${lockedParentContext.parentTitle}`,
        itemTitle: lockedParentContext.parentTitle,
        owner: (() => {
          const ownerRaw = parentItem?.owner;
          const ownerString = ownerRaw === null || ownerRaw === undefined ? '' : ownerRaw.toString().trim();
          return ownerString || 'Unassigned';
        })(),
        description: (() => {
          const descriptionRaw = parentItem?.description;
          return descriptionRaw === null || descriptionRaw === undefined ? '' : descriptionRaw.toString().trim();
        })(),
        isChild: false
      };
    }

    const fallbackItem = items.find(item => normalizeId(item.id) === normalizedSelectedParentId);
    if (fallbackItem) {
      const constructed = createParentOption(fallbackItem);
      if (constructed) {
        return constructed;
      }
    }

    return null;
  }, [parentItemOptions, normalizedSelectedParentId, lockedParentContext, items]);

  const selectedParentIsChild = selectedParentOption?.isChild === true;

  // Reset modal when it closes
  useEffect(() => {
    if (!show) {
      setSelectedParentId(normalizeId(preSelectedParentId));
      setDescription('');
      setDueDate('');
      setAssignedTo(null);
      setIsLockedToParent(false);
      setLockedParentContext(null);
    }
  }, [show, preSelectedParentId]);

  // Set preselected parent when provided and modal is shown
  useEffect(() => {
    if (show && preSelectedParentId !== undefined && preSelectedParentId !== null && preSelectedParentId !== '') {
      const normalizedPreselected = normalizeId(preSelectedParentId);

      if (normalizedPreselected === null) {
        setSelectedParentId(null);
        setIsLockedToParent(false);
        setLockedParentContext(null);
        return;
      }

      const selectedItem = items.find(item => normalizeId(item.id) === normalizedPreselected);
      const childOfValues = parseChildOf(selectedItem?.child_of);
      const parentId = childOfValues.length > 0 ? normalizeId(childOfValues[0]) : null;

      if (parentId) {
        const parentItem = items.find(item => normalizeId(item.id) === parentId);
        setSelectedParentId(parentId);
        setIsLockedToParent(true);
        setLockedParentContext({
          childId: normalizedPreselected,
          parentId,
          parentTitle: parentItem?.title || `Item ${formatItemRef(parentId)}`
        });
      } else {
        setSelectedParentId(normalizedPreselected);
        setIsLockedToParent(false);
        setLockedParentContext(null);
      }
    } else if (show) {
      setSelectedParentId(null);
      setIsLockedToParent(false);
      setLockedParentContext(null);
    }
  }, [show, preSelectedParentId, items]);

  // Determine if the currently selected item is a child and provide guidance
  const childWarning = useMemo(() => {
    if (!show) {
      return { isChild: false, message: null };
    }

    if (lockedParentContext) {
      return {
        isChild: true,
        message: `Item ${formatItemRef(lockedParentContext.childId)} is linked to parent ${formatItemRef(lockedParentContext.parentId)} - ${lockedParentContext.parentTitle}. Parent selection is locked to ensure the task is created on the parent.`
      };
    }

    if (!normalizedSelectedParentId || items.length === 0) {
      return { isChild: false, message: null };
    }

    const selectedOption = parentItemOptions.find(option => option.value === normalizedSelectedParentId);
    if (selectedOption?.isChild) {
      return {
        isChild: true,
        message: 'The selected item is a child. Please choose its parent instead.'
      };
    }

    return { isChild: false, message: null };
  }, [show, lockedParentContext, normalizedSelectedParentId, items, parentItemOptions]);

  const handleConfirmCreateTask = async () => {
    if (!normalizedSelectedParentId || !description.trim()) {
      if (onTaskCreated) {
        onTaskCreated('Please select a parent item and enter a description', 'error');
      }
      return;
    }

    if (selectedParentIsChild) {
      if (onTaskCreated) {
        onTaskCreated('Tasks can only be created for parent items. Please choose a parent item.', 'error');
      }
      return;
    }

    if (dueDate && !isValidDateInputValue(dueDate)) {
      if (onTaskCreated) {
        onTaskCreated('Please enter a valid due date between 1900 and 9999', 'error');
      }
      return;
    }

    try {
      const taskData = {
        parent_id: normalizedSelectedParentId,
        description: description.trim(),
        status: 'Assigned'
      };

      // Add assigned_to if provided
      if (assignedTo) {
        // Extract the actual value from the Select component's option object
        taskData.assigned_to = typeof assignedTo === 'object' ? assignedTo.value : assignedTo;
      }

      // Add due_date if provided
      if (dueDate) {
        taskData.due_date = dueDate;
      }

      const response = await createTaskMutation.mutateAsync(taskData);

      if (onTaskCreated) {
        onTaskCreated('Task created successfully', 'success');
      }
      
      handleClose();

      const target = typeof taskDetailUrlBuilder === 'function'
        ? taskDetailUrlBuilder(response.id, {
          parentId: normalizedSelectedParentId,
          parentTitle: selectedParentOption?.itemTitle || preSelectedParentTitle
        })
        : `/tasks/${response.id}`;

      navigate(target);
    } catch (error) {
      if (onTaskCreated) {
        onTaskCreated('Failed to create task', 'error');
      }
      console.error('Error creating task:', error);
    }
  };

  const handleClose = useCallback(() => {
    setSelectedParentId(normalizeId(preSelectedParentId));
    setDescription('');
    setDueDate('');
    setAssignedTo(null);
    setIsLockedToParent(false);
    setLockedParentContext(null);
    closeModal();
  }, [preSelectedParentId, closeModal]);

  return createPortal(
    <Modal
      show={show}
      onCancel={handleClose}
      onConfirm={handleConfirmCreateTask}
      title="Create New Task"
      confirmText={createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
      cancelText="Cancel"
      confirmDisabled={!normalizedSelectedParentId || !description.trim() || selectedParentIsChild}
      loading={createTaskMutation.isPending}
      size="modal-lg"
      content={
        <div>
          {childWarning.isChild && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              {childWarning.message}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Parent Item <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              className="inline-edit-select"
              value={selectedParentOption}
              onChange={(selectedOption) => {
                setSelectedParentId(selectedOption ? normalizeId(selectedOption.value) : null);
              }}
              options={parentItemOptions}
              isClearable={!isLockedToParent}
              isDisabled={isLockedToParent}
              isOptionDisabled={(option) => option.isChild}
              placeholder={isLockedToParent && lockedParentContext ?
                `Locked to parent ${formatItemRef(lockedParentContext.parentId)}` :
                preSelectedParentTitle ?
                  `Pre-selected: ${preSelectedParentTitle}` :
                  "Select an item..."
              }
              formatOptionLabel={(option, { context }) => {
                const isChild = option.isChild;
                const ownerDisplay = option.owner || 'Unassigned';
                const normalizedDescription = option.description ? option.description.replace(/\s+/g, ' ').trim() : '';
                const truncatedDescription = normalizedDescription.length > 160 ? `${normalizedDescription.slice(0, 157)}...` : normalizedDescription;

                if (context === 'value') {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                      <span style={{ fontWeight: 600, color: '#1f2937' }}>
                        #{option.value} <span style={{ fontWeight: 500, color: '#374151' }}>{option.itemTitle}</span>
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        Owner: {ownerDisplay}
                      </span>
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: isChild ? 0.55 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: isChild ? '#6b7280' : '#111827' }}>
                      <span>{formatItemRef(option.value)}</span>
                      <span>{option.itemTitle}</span>
                      {isChild && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#b45309',
                            fontWeight: 600,
                            backgroundColor: '#fef3c7',
                            padding: '2px 6px',
                            borderRadius: '9999px'
                          }}
                        >
                          Child Item
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#4b5563', fontWeight: 500 }}>
                      Owner: {ownerDisplay}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {truncatedDescription || <em style={{ color: '#9ca3af' }}>No description provided</em>}
                    </div>
                  </div>
                );
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: '48px',
                  borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#9ca3af'
                  }
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => {
                  const isChild = state.data?.isChild;
                  return {
                    ...provided,
                    cursor: isChild ? 'not-allowed' : 'pointer',
                    backgroundColor: state.isSelected
                      ? '#e2e8f0'
                      : state.isFocused
                        ? (isChild ? '#fef3c7' : '#f1f5f9')
                        : '#ffffff',
                    color: isChild ? '#9ca3af' : '#1f2937',
                    opacity: isChild ? 0.6 : 1
                  };
                },
                singleValue: (provided) => ({
                  ...provided,
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.3
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#94a3b8'
                })
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Task Description <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter task description..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Assigned To
            </label>
            <Select
              options={userOptions}
              value={assignedTo}
              onChange={setAssignedTo}
              placeholder={usersLoading ? "Loading users..." : usersError ? "Error loading users" : "Select a user to assign this task to..."}
              isClearable
              isLoading={usersLoading}
              isDisabled={usersError}
              noOptionsMessage={() => usersError ? "Failed to load users" : "No users available"}
              menuPortalTarget={document.body}
              styles={{
                control: base => ({ 
                  ...base, 
                  fontSize: '14px',
                  minHeight: '38px' 
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Due Date
            </label>
            <div 
              style={{ 
                width: '100%',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={DATE_INPUT_MIN}
                max={DATE_INPUT_MAX}
                {...datePickerTriggers}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  background: 'transparent'
                }}
              />
            </div>
          </div>
        </div>
      }
    />,
    document.body
  );
};

export default CreateTaskModal;